import { Connection, PublicKey, TransactionInstruction } from '@solana/web3.js';
import {
  getMint,
  TOKEN_PROGRAM_ID,
  TOKEN_2022_PROGRAM_ID,
  getAssociatedTokenAddress,
  createSetAuthorityInstruction,
  AuthorityType,
  createMintToInstruction,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  MINT_SIZE,
  MintLayout,
  ACCOUNT_SIZE,
} from '@solana/spl-token';
import {
  createUpdateMetadataAccountV2Instruction,
  PROGRAM_ID as TOKEN_METADATA_PROGRAM_ID,
} from '@metaplex-foundation/mpl-token-metadata';
import { createUpdateAuthorityInstruction } from '@solana/spl-token-metadata';

/**
 * Detect whether a mint is a Token 2022 or Token v1 mint by checking the account owner.
 */
export async function detectTokenProgramId(connection: Connection, mintAddress: PublicKey): Promise<PublicKey> {
  const accountInfo = await connection.getAccountInfo(mintAddress);
  if (!accountInfo) { throw new Error('Mint account not found'); }
  if (accountInfo.owner.equals(TOKEN_2022_PROGRAM_ID)) { return TOKEN_2022_PROGRAM_ID; }
  return TOKEN_PROGRAM_ID;
}

/**
 * Get mint and freeze authorities from a token mint account
 */
export async function getTokenAuthorities(connection: Connection, mintAddress: PublicKey, programId?: PublicKey) {
  try {
    const resolvedProgramId = programId ?? await detectTokenProgramId(connection, mintAddress);
    const mintInfo = await getMint(connection, mintAddress, 'confirmed', resolvedProgramId);
    return {
      mintAuthority: mintInfo.mintAuthority,
      freezeAuthority: mintInfo.freezeAuthority,
      supply: mintInfo.supply,
      decimals: mintInfo.decimals,
    };
  } catch (error) {
    console.error('Error getting token authorities:', error);
    throw error;
  }
}

/**
 * Get mint and freeze authorities from a token mint account using secureRPC
 * This version uses the secure RPC proxy (Helius/Alchemy) instead of direct connection
 */
export async function getTokenAuthoritiesSecure(
  getAccountInfo: (publicKey: string) => Promise<any>,
  mintAddress: PublicKey
) {
  try {
    const accountInfo = await getAccountInfo(mintAddress.toString());
    
    // Handle different response formats
    // Solana RPC returns: { context: {...}, value: {...} } or { context: {...}, value: null }
    // Our secureRPC.callRPC returns data.result, which is the full result object
    let accountValue = null;
    
    if (!accountInfo) {
      throw new Error('Mint account not found');
    }
    
    if (typeof accountInfo === 'object') {
      if ('value' in accountInfo) {
        // Standard Solana RPC format: { context: {...}, value: {...} }
        accountValue = accountInfo.value;
      } else if ('data' in accountInfo) {
        // Direct account format: { data: [...], executable: false, ... }
        accountValue = accountInfo;
      }
    }
    
    if (!accountValue) {
      throw new Error('Mint account not found');
    }
    
    // Extract data from account value
    // accountValue should be: { data: [...], executable: false, lamports: ..., owner: "..." }
    const {data} = accountValue;
    
    if (!data) {
      throw new Error(`Account data is missing. Full response: ${JSON.stringify(accountInfo, null, 2)}`);
    }

    // Deserialize mint account data
    // Mint account structure (82 bytes total):
    // - mintAuthorityOption: u8 (1 byte) - 0 if null, 1 if present
    // - mintAuthority: Pubkey (32 bytes) if present, else skip
    // - supply: u64 (8 bytes)
    // - decimals: u8 (1 byte)
    // - isInitialized: u8 (1 byte)
    // - freezeAuthorityOption: u8 (1 byte) - 0 if null, 1 if present
    // - freezeAuthority: Pubkey (32 bytes) if present, else skip
    let buffer: Buffer;
    
    // Handle different data formats from RPC
    if (Array.isArray(data)) {
      // Format: { data: ["base64string"], encoding: "base64" }
      buffer = Buffer.from(data[0], 'base64');
    } else if (typeof data === 'string') {
      // Format: { data: "base64string", encoding: "base64" }
      buffer = Buffer.from(data, 'base64');
    } else {
      throw new Error(`Invalid account data format: ${JSON.stringify(data)}`);
    }
    
    if (buffer.length < MINT_SIZE) {
      throw new Error(`Invalid mint account size: ${buffer.length} bytes, expected at least ${MINT_SIZE}`);
    }

    // Use SPL token library's built-in deserialization for accurate parsing
    // This ensures we match exactly what Solana Explorer shows
    const rawMint = MintLayout.decode(buffer.slice(0, MINT_SIZE));
    
    // Extract authorities - MintLayout handles the Option<Pubkey> deserialization correctly
    const mintAuthority = rawMint.mintAuthorityOption ? new PublicKey(rawMint.mintAuthority) : null;
    const freezeAuthority = rawMint.freezeAuthorityOption ? new PublicKey(rawMint.freezeAuthority) : null;

    const ownerStr = accountValue.owner;
    let programId = TOKEN_PROGRAM_ID;
    if (typeof ownerStr === 'string') {
      try {
        const ownerPk = new PublicKey(ownerStr);
        if (ownerPk.equals(TOKEN_2022_PROGRAM_ID)) {programId = TOKEN_2022_PROGRAM_ID;}
      } catch { /* ignore — fall back to Token v1 */ }
    }

    return {
      mintAuthority,
      freezeAuthority,
      supply: rawMint.supply,
      decimals: rawMint.decimals,
      programId,
      rawData: buffer,
    };
  } catch (error) {
    console.error('Error getting token authorities:', error);
    throw error;
  }
}

/**
 * Fetch token metadata account
 */
export async function getTokenMetadata(connection: Connection, mintAddress: PublicKey) {
  try {
    const [metadataAddress] = await PublicKey.findProgramAddress(
      [
        Buffer.from('metadata'),
        TOKEN_METADATA_PROGRAM_ID.toBuffer(),
        mintAddress.toBuffer(),
      ],
      TOKEN_METADATA_PROGRAM_ID
    );

    const metadataAccountInfo = await connection.getAccountInfo(metadataAddress);
    
    if (!metadataAccountInfo) {
      return null;
    }

    // Parse metadata using Metaplex format
    // The metadata account structure: key (1 byte) + data
    // We need to deserialize the data according to Metaplex Token Metadata standard
    // For now, we'll return the raw data and parse isMutable from it
    // The isMutable flag is typically at a specific offset in the account data
    
    // Metaplex metadata v3 structure:
    // - key: u8 (1 byte)
    // - update_authority: Pubkey (32 bytes)
    // - mint: Pubkey (32 bytes)
    // - data: DataV2
    // - is_mutable: bool (1 byte) - typically around offset 1 + 32 + 32 + data_length
    
    // For simplicity, we'll fetch the account and let the component handle parsing
    // Or we can use a library to deserialize, but for now return the account info
    return {
      address: metadataAddress,
      accountInfo: metadataAccountInfo,
    };
  } catch (error) {
    console.error('Error getting token metadata:', error);
    throw error;
  }
}

/**
 * Fetch token metadata account using secureRPC
 * This version uses the secure RPC proxy (Helius/Alchemy) instead of direct connection
 */
export async function getTokenMetadataSecure(
  getAccountInfo: (publicKey: string) => Promise<any>,
  mintAddress: PublicKey
) {
  try {
    const [metadataAddress] = await PublicKey.findProgramAddress(
      [
        Buffer.from('metadata'),
        TOKEN_METADATA_PROGRAM_ID.toBuffer(),
        mintAddress.toBuffer(),
      ],
      TOKEN_METADATA_PROGRAM_ID
    );

    const accountInfo = await getAccountInfo(metadataAddress.toString());
    
    if (!accountInfo || !accountInfo.value) {
      return null;
    }

    // Convert the account info to a format similar to connection.getAccountInfo
    const metadataAccountInfo = {
      data: accountInfo.value.data,
      executable: accountInfo.value.executable,
      lamports: accountInfo.value.lamports,
      owner: new PublicKey(accountInfo.value.owner),
    };

    return {
      address: metadataAddress,
      accountInfo: metadataAccountInfo,
    };
  } catch (error) {
    console.error('Error getting token metadata:', error);
    throw error;
  }
}

// Token-2022 TLV constants. The mint buffer layout is:
// [base Mint] padded to ACCOUNT_SIZE | 1-byte AccountType | TLV entries.
// Each TLV entry: u16 type LE + u16 length LE + `length` bytes of data.
const ACCOUNT_TYPE_SIZE = 1;
const TLV_HEADER_LEN = 4;
const EXT_TYPE_METADATA_POINTER = 18;
const EXT_TYPE_TOKEN_METADATA = 19;

function isZeroPubkeyBytes(bytes: Buffer): boolean {
  for (let i = 0; i < bytes.length; i++) {
    if (bytes[i] !== 0) {return false;}
  }
  return true;
}

function findExtensionData(mintBuffer: Buffer, extensionType: number): Buffer | null {
  const tlvStart = ACCOUNT_SIZE + ACCOUNT_TYPE_SIZE;
  if (mintBuffer.length <= tlvStart) {return null;}

  let cursor = tlvStart;
  while (cursor + TLV_HEADER_LEN <= mintBuffer.length) {
    const type = mintBuffer.readUInt16LE(cursor);
    const length = mintBuffer.readUInt16LE(cursor + 2);
    const dataStart = cursor + TLV_HEADER_LEN;
    const dataEnd = dataStart + length;
    if (dataEnd > mintBuffer.length) {return null;}

    if (type === extensionType) {
      return mintBuffer.slice(dataStart, dataEnd);
    }

    if (type === 0 && length === 0) {
      cursor += TLV_HEADER_LEN;
      continue;
    }
    cursor = dataEnd;
  }
  return null;
}

/**
 * Parse the Token-2022 TokenMetadata extension from a mint buffer.
 * Layout (see @solana/spl-token-metadata):
 *   updateAuthority (32) + mint (32) + u32-len name + u32-len symbol +
 *   u32-len uri + additionalMetadata.
 * Token-2022 metadata has no separate `isMutable` flag — by spec the
 * metadata is immutable iff updateAuthority is the zero pubkey (None).
 */
export function parseTokenMetadataExtensionFromMint(
  mintBuffer: Buffer,
): { updateAuthority: PublicKey | null; name: string; symbol: string; uri: string } | null {
  const data = findExtensionData(mintBuffer, EXT_TYPE_TOKEN_METADATA);
  if (!data || data.length < 64) {return null;}

  const updateAuthorityBytes = data.slice(0, 32);
  const updateAuthority = isZeroPubkeyBytes(updateAuthorityBytes)
    ? null
    : new PublicKey(updateAuthorityBytes);

  let cursor = 64;
  const readStr = (): string => {
    if (cursor + 4 > data.length) {return '';}
    const len = data.readUInt32LE(cursor);
    cursor += 4;
    if (cursor + len > data.length) {return '';}
    const out = data.slice(cursor, cursor + len).toString('utf8');
    cursor += len;
    return out;
  };
  const name = readStr();
  const symbol = readStr();
  const uri = readStr();

  return { updateAuthority, name, symbol, uri };
}

/**
 * Parse the Token-2022 MetadataPointer extension from a mint buffer.
 * Layout: authority (32) + metadataAddress (32). All-zero == None.
 */
export function parseMetadataPointerFromMint(
  mintBuffer: Buffer,
): { authority: PublicKey | null; metadataAddress: PublicKey | null } | null {
  const data = findExtensionData(mintBuffer, EXT_TYPE_METADATA_POINTER);
  if (!data || data.length < 64) {return null;}
  const authBytes = data.slice(0, 32);
  const addrBytes = data.slice(32, 64);
  return {
    authority: isZeroPubkeyBytes(authBytes) ? null : new PublicKey(authBytes),
    metadataAddress: isZeroPubkeyBytes(addrBytes) ? null : new PublicKey(addrBytes),
  };
}

export type TokenMetadataSource = 'token-2022-extension' | 'metaplex' | 'none';

export interface ResolvedTokenMetadata {
  exists: boolean;
  source: TokenMetadataSource;
  updateAuthority: PublicKey | null;
  isMutable: boolean;
}

/**
 * Resolve token metadata for a mint. Checks the Token-2022 embedded
 * TokenMetadata extension first (the thing Solana Explorer and wallets
 * read for Token-2022 mints), then falls back to the classic Metaplex
 * Token Metadata PDA. Pass `rawMintData` and `programId` from
 * getTokenAuthoritiesSecure so we don't re-fetch the mint.
 */
export async function resolveTokenMetadataSecure(
  getAccountInfo: (publicKey: string) => Promise<any>,
  mintAddress: PublicKey,
  rawMintData: Buffer | null | undefined,
  programId: PublicKey,
): Promise<ResolvedTokenMetadata> {
  if (rawMintData && programId.equals(TOKEN_2022_PROGRAM_ID)) {
    const embedded = parseTokenMetadataExtensionFromMint(rawMintData);
    if (embedded) {
      return {
        exists: true,
        source: 'token-2022-extension',
        updateAuthority: embedded.updateAuthority,
        isMutable: embedded.updateAuthority !== null,
      };
    }

    // MetadataPointer aimed at an external account (Metaplex PDA or other).
    const pointer = parseMetadataPointerFromMint(rawMintData);
    if (pointer && pointer.metadataAddress && !pointer.metadataAddress.equals(mintAddress)) {
      try {
        const info = await getAccountInfo(pointer.metadataAddress.toString());
        const value = info && typeof info === 'object' && 'value' in info ? (info as any).value : info;
        if (value) {
          const owner = typeof value.owner === 'string' ? new PublicKey(value.owner) : value.owner;
          if (owner && owner.equals(TOKEN_METADATA_PROGRAM_ID)) {
            const parsed = parseMetadataAccount({ data: value.data });
            return {
              exists: true,
              source: 'metaplex',
              updateAuthority: parsed.updateAuthority,
              isMutable: parsed.isMutable,
            };
          }
        }
      } catch (err) {
        console.warn('MetadataPointer resolution failed:', err);
      }
    }
  }

  const metaplex = await getTokenMetadataSecure(getAccountInfo, mintAddress);
  if (metaplex) {
    const parsed = parseMetadataAccount(metaplex.accountInfo);
    return {
      exists: true,
      source: 'metaplex',
      updateAuthority: parsed.updateAuthority,
      isMutable: parsed.isMutable,
    };
  }

  return { exists: false, source: 'none', updateAuthority: null, isMutable: false };
}

/**
 * Parse metadata account to extract update authority and mutability
 * Metaplex Token Metadata structure:
 * - Offset 0: key (1 byte)
 * - Offset 1: update_authority (32 bytes)
 * - Offset 33: mint (32 bytes)
 * - Offset 65: data (variable length - name, symbol, uri, seller_fee_basis_points, creators)
 * - After data: is_mutable (1 byte) - this is the last byte before padding
 */
export function parseMetadataAccount(metadataAccountInfo: any): { updateAuthority: PublicKey | null; isMutable: boolean } {
  if (!metadataAccountInfo || !metadataAccountInfo.data) {
    return { updateAuthority: null, isMutable: false };
  }

  try {
    let buffer: Buffer;
    const {data} = metadataAccountInfo;
    
    // Handle different data formats from RPC
    if (Array.isArray(data)) {
      buffer = Buffer.from(data[0], 'base64');
    } else if (typeof data === 'string') {
      buffer = Buffer.from(data, 'base64');
    } else {
      return { updateAuthority: null, isMutable: true }; // Default to mutable if we can't parse
    }
    
    if (buffer.length < 66) {
      return { updateAuthority: null, isMutable: true }; // Not enough data, default to mutable
    }
    
    // Extract update authority
    // In Metaplex, update authority is stored as an Option<Pubkey>
    // The first byte at offset 1 indicates if it exists (0 = None, 1 = Some)
    // If it's 0, the update authority is revoked/null
    // If it's 1, the next 32 bytes contain the PublicKey
    const optionByte = buffer[1];
    
    let updateAuthority: PublicKey | null = null;
    if (optionByte === 1 && buffer.length >= 34) {
      // Update authority exists, extract it (offset 2-34)
      const updateAuthorityBytes = buffer.slice(2, 34);
      updateAuthority = new PublicKey(updateAuthorityBytes);
    } else if (optionByte === 0) {
      // Update authority is revoked (None)
      updateAuthority = null;
    } else {
      // Fallback: try to parse from offset 1-33 (old format or different structure)
      const updateAuthorityBytes = buffer.slice(1, 33);
      try {
        const updateAuthorityPubkey = new PublicKey(updateAuthorityBytes);
        // Check if it's the burn address (common way to revoke)
        const burnAddress = new PublicKey('11111111111111111111111111111111');
        updateAuthority = updateAuthorityPubkey.equals(burnAddress) ? null : updateAuthorityPubkey;
      } catch {
        updateAuthority = null;
      }
    }
    
    // Find isMutable byte (last non-zero byte before padding)
    let lastNonZeroIndex = buffer.length - 1;
    while (lastNonZeroIndex > 0 && buffer[lastNonZeroIndex] === 0) {
      lastNonZeroIndex--;
    }
    
    // Check possible positions for isMutable
    const possibleIsMutablePositions = [
      lastNonZeroIndex,
      lastNonZeroIndex - 1,
      buffer.length - 1,
      buffer.length - 2,
    ];
    
    let isMutable = true; // Default to mutable
    for (const pos of possibleIsMutablePositions) {
      if (pos >= 65 && pos < buffer.length) {
        const isMutableByte = buffer[pos];
        if (isMutableByte === 0 || isMutableByte === 1) {
          if (pos > 0 && (buffer[pos - 1] === 0 || pos === buffer.length - 1)) {
            isMutable = isMutableByte === 1;
            break;
          }
        }
      }
    }
    
    // Fallback: check last few bytes
    for (let i = buffer.length - 1; i >= Math.max(65, buffer.length - 10); i--) {
      const byte = buffer[i];
      if (byte === 0 || byte === 1) {
        if (i > 0 && (buffer[i - 1] === 0 || i === buffer.length - 1)) {
          isMutable = byte === 1;
          break;
        }
      }
    }
    
    return { updateAuthority, isMutable };
  } catch (error) {
    console.error('Error parsing metadata account:', error);
    return { updateAuthority: null, isMutable: true }; // Default to mutable if we can't determine
  }
}

/**
 * Check if metadata is mutable by parsing the account data
 * Metaplex Token Metadata structure:
 * - Offset 0: key (1 byte)
 * - Offset 1: update_authority (32 bytes)
 * - Offset 33: mint (32 bytes)
 * - Offset 65: data (variable length - name, symbol, uri, seller_fee_basis_points, creators)
 * - After data: is_mutable (1 byte) - this is the last byte before padding
 */
export function isMetadataMutable(metadataAccountInfo: any): boolean {
  const parsed = parseMetadataAccount(metadataAccountInfo);
  return parsed.isMutable;
}

/**
 * Create a transaction instruction to revoke mint authority
 */
export function createRevokeMintAuthorityInstruction(
  mintAddress: PublicKey,
  currentAuthority: PublicKey,
  programId: PublicKey = TOKEN_PROGRAM_ID
): TransactionInstruction {
  return createSetAuthorityInstruction(
    mintAddress,
    currentAuthority,
    AuthorityType.MintTokens,
    null,
    [],
    programId
  );
}

/**
 * Create a transaction instruction to revoke freeze authority
 */
export function createRevokeFreezeAuthorityInstruction(
  mintAddress: PublicKey,
  currentAuthority: PublicKey,
  programId: PublicKey = TOKEN_PROGRAM_ID
): TransactionInstruction {
  return createSetAuthorityInstruction(
    mintAddress,
    currentAuthority,
    AuthorityType.FreezeAccount,
    null,
    [],
    programId
  );
}

/**
 * Create a transaction instruction to revoke update authority using secureRPC
 * This version uses the secure RPC proxy (Helius/Alchemy) instead of direct connection
 */
export async function createRevokeUpdateAuthorityInstructionSecure(
  getAccountInfo: (publicKey: string) => Promise<any>,
  mintAddress: PublicKey,
  updateAuthority: PublicKey
): Promise<TransactionInstruction> {
  const [metadataAddress] = await PublicKey.findProgramAddress(
    [
      Buffer.from('metadata'),
      TOKEN_METADATA_PROGRAM_ID.toBuffer(),
      mintAddress.toBuffer(),
    ],
    TOKEN_METADATA_PROGRAM_ID
  );

  // Burn address used to indicate revoked authority
  const burnAddress = new PublicKey('11111111111111111111111111111111');

  return createUpdateMetadataAccountV2Instruction(
    {
      metadata: metadataAddress,
      updateAuthority,
    },
    {
      updateMetadataAccountArgsV2: {
        data: null, // keep existing metadata
        isMutable: false, // make immutable
        updateAuthority: burnAddress, // set to burn address (revoked)
        primarySaleHappened: null,
      },
    }
  );
}

/**
 * Create a transaction instruction to revoke the update authority of
 * Token-2022 metadata stored in the mint's TokenMetadata extension.
 * Setting it to None makes the metadata permanently immutable.
 */
export function createRevokeTokenMetadataUpdateAuthorityInstruction(
  mintAddress: PublicKey,
  updateAuthority: PublicKey
): TransactionInstruction {
  return createUpdateAuthorityInstruction({
    programId: TOKEN_2022_PROGRAM_ID,
    metadata: mintAddress,
    oldAuthority: updateAuthority,
    newAuthority: null,
  });
}

/**
 * Create a transaction instruction to mint additional tokens to a wallet
 */
export async function createMintAdditionalTokensInstruction(
  mintAddress: PublicKey,
  destinationWallet: PublicKey,
  amount: bigint,
  mintAuthority: PublicKey,
  programId: PublicKey = TOKEN_PROGRAM_ID
): Promise<{ instruction: TransactionInstruction; associatedTokenAccount: PublicKey }> {
  const associatedTokenAccount = await getAssociatedTokenAddress(
    mintAddress,
    destinationWallet,
    false,
    programId,
    ASSOCIATED_TOKEN_PROGRAM_ID
  );

  const instruction = createMintToInstruction(
    mintAddress,
    associatedTokenAccount,
    mintAuthority,
    amount,
    [],
    programId
  );

  return {
    instruction,
    associatedTokenAccount,
  };
} 